import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchShippingSettings,
  getCachedShippingSettings,
  calculateShipping,
} from '../utils/shippingConfig';
import {
  clearGuestCart,
  updateGuestCartQty,
  removeGuestCartItem,
  saveUserCartCache,
  getUserCartCache,
  getUnifiedCart,
} from '../utils/cartHelper';
import { Trash2 } from 'lucide-react';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://api.vanyacollectionsrajahmundry.com';

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const locationState = location.state || {};
  const appliedCoupon = locationState.appliedCoupon || null;

  const [cartItems, setCartItems] = useState(() => {
    if (Array.isArray(locationState.items) && locationState.items.length > 0) {
      return locationState.items;
    }
    return getUnifiedCart();
  });

  const subtotal = cartItems.reduce(
    (acc, item) => acc + (Number(item.price) || 0) * (Number(item.quantity) || 1),
    0
  );

  const [shippingSettings, setShippingSettings] = useState(getCachedShippingSettings);

  useEffect(() => {
    const loadShipping = async () => {
      try {
        const s = await fetchShippingSettings();
        if (s) setShippingSettings(s);
      } catch (e) {
        console.warn('Failed to fetch shipping settings:', e);
      }
    };
    loadShipping();

    const handleStorageChange = () => {
      setShippingSettings(getCachedShippingSettings());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Discount calculation
  const discountAmount =
    appliedCoupon && appliedCoupon.min_amount && subtotal < Number(appliedCoupon.min_amount)
      ? 0
      : locationState.discountAmount !== undefined
      ? Number(locationState.discountAmount)
      : appliedCoupon
      ? appliedCoupon.discount_type === 'percentage'
        ? (subtotal * parseFloat(appliedCoupon.discount_value)) / 100
        : parseFloat(appliedCoupon.discount_value)
      : 0;

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  });

  const [formData, setFormData] = useState(() => {
    let initialUser = null;
    try {
      const stored = localStorage.getItem('user');
      if (stored) initialUser = JSON.parse(stored);
    } catch (e) {}

    const cleanUserPhone = initialUser?.phone
      ? String(initialUser.phone).replace(/\D/g, '').slice(-10)
      : '';

    return {
      fullName: initialUser?.fullName || initialUser?.name || '',
      phone: cleanUserPhone,
      altPhone: '',
      email: initialUser?.email || '',
      pinCode: '',
      city: '',
      state: '',
      address: initialUser?.address || '',
    };
  });

  // Pincode auto-lookup state
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeMessage, setPincodeMessage] = useState(null);

  // Total pieces across all items in cart
  const totalPieces = cartItems.reduce(
    (acc, it) => acc + (Number(it.quantity) || Number(it.qty) || 1),
    0
  );

  // Shipping calculation based on destination state and piece count
  const shippingInfo = calculateShipping(
    subtotal,
    shippingSettings,
    formData.state,
    totalPieces
  );
  const shippingFee = cartItems.length > 0 ? shippingInfo.shippingCost : 0;
  const isFreeShipping = shippingInfo.isFree;

  // Grand total
  const grandTotal = Math.max(0, subtotal - discountAmount) + shippingFee;

  const [step, setStep] = useState('address');
  const [loading, setLoading] = useState(false);
  const [copiedOrderId, setCopiedOrderId] = useState(false);

  // Out of stock tracking
  const [outOfStockItemIds, setOutOfStockItemIds] = useState(new Set());

  // Unique session ID for concurrency locking
  const [checkoutSessionId] = useState(() => {
    let sess = sessionStorage.getItem('vanya_checkout_session');
    if (!sess) {
      sess = 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
      sessionStorage.setItem('vanya_checkout_session', sess);
    }
    return sess;
  });

  // Gateway queue state (Proceeding to payment gateway buffer screen)
  const [gatewayQueue, setGatewayQueue] = useState({
    active: false,
    countdown: 180,
  });

  // Out of stock item modal state (shows saree photo, name, and out of stock status)
  const [outOfStockModal, setOutOfStockModal] = useState({
    show: false,
    item: null,
  });

  const queueIntervalRef = useRef(null);
  const queueTimerRef = useRef(null);

  // Validation Popup state
  const [validationPopup, setValidationPopup] = useState({
    show: false,
    message: '',
    type: 'error',
    actionItem: null,
  });

  // Gorgeous Success Popup state
  const [successPopup, setSuccessPopup] = useState({
    show: false,
    orderNumber: '',
    orderId: '',
    totalAmount: 0,
    paidPrice: 0,
    paymentId: '',
    customerName: '',
    city: '',
    countdown: 5,
  });

  // If user is logged in, optionally fetch latest profile address/phone if missing
  useEffect(() => {
    if (currentUser?.id) {
      fetch(`${API_BASE_URL}/auth/users/${currentUser.id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data) {
            setFormData((prev) => ({
              ...prev,
              fullName: prev.fullName || data.full_name || currentUser.fullName || '',
              phone: prev.phone || (data.phone ? String(data.phone).replace(/\D/g, '').slice(-10) : ''),
              email: prev.email || data.email || currentUser.email || '',
              address: prev.address || data.address || '',
            }));
          }
        })
        .catch(() => {});
    }
  }, [currentUser]);

  // Handle PIN code input and auto-detect city & state via India Post API
  const handlePincodeChange = async (pinValue) => {
    const cleanPin = pinValue.replace(/\D/g, '').slice(0, 6);
    setFormData((prev) => ({
      ...prev,
      pinCode: cleanPin,
    }));

    if (cleanPin.length === 6) {
      try {
        setPincodeLoading(true);
        setPincodeMessage(null);
        const res = await fetch(`https://api.postalpincode.in/pincode/${cleanPin}`);
        const data = await res.json();
        if (Array.isArray(data) && data[0]?.Status === 'Success') {
          const poList = data[0].PostOffice;
          if (Array.isArray(poList) && poList.length > 0) {
            const firstPo = poList[0];
            const detectedCity = firstPo.District || firstPo.Division || firstPo.Block || '';
            const detectedState = firstPo.State || '';

            setFormData((prev) => ({
              ...prev,
              city: detectedCity || prev.city,
              state: detectedState || prev.state,
            }));
            setPincodeMessage({
              type: 'success',
              text: `📍 ${detectedCity ? detectedCity + ', ' : ''}${detectedState} auto-detected`,
            });
          }
        } else {
          setPincodeMessage({
            type: 'error',
            text: 'Could not auto-detect location. Please enter City & State manually.',
          });
        }
      } catch (err) {
        console.warn('Pincode lookup error:', err);
      } finally {
        setPincodeLoading(false);
      }
    } else {
      setPincodeMessage(null);
    }
  };

  const redirectToTracking = useCallback(
    (targetOrderId) => {
      const finalId = targetOrderId || successPopup.orderNumber || successPopup.orderId;
      navigate(`/track-order?order_id=${finalId}&placed=true`, {
        state: {
          orderId: finalId,
          orderNumber: finalId,
          isNewOrder: true,
          paymentStatus: 'paid',
          orderStatus: 'confirmed',
          totalAmount: grandTotal,
        },
      });
    },
    [navigate, successPopup.orderNumber, successPopup.orderId, grandTotal]
  );

  // Countdown timer for automatic redirect on success
  useEffect(() => {
    let timer;
    if (successPopup.show && successPopup.countdown > 0) {
      timer = setTimeout(() => {
        setSuccessPopup((prev) => ({
          ...prev,
          countdown: prev.countdown - 1,
        }));
      }, 1000);
    } else if (successPopup.show && successPopup.countdown === 0) {
      redirectToTracking(successPopup.orderNumber || successPopup.orderId);
    }
    return () => clearTimeout(timer);
  }, [successPopup.show, successPopup.countdown, successPopup.orderNumber, successPopup.orderId, redirectToTracking]);

  const showValidationError = (message, actionItem = null) => {
    setValidationPopup({
      show: true,
      message,
      type: 'error',
      actionItem,
    });
  };

  // ==========================================================
  // CART QUANTITY & REMOVE HANDLERS (CHECKOUT PAGE)
  // ==========================================================

  const handleIncreaseQty = async (index) => {
    const item = cartItems[index];
    if (!item) return;

    const currentQty = Number(item.quantity || 1);
    const maxStock =
      item.variant?.stock !== undefined && item.variant?.stock !== null
        ? Number(item.variant.stock)
        : (item.stock !== undefined && item.stock !== null ? Number(item.stock) : null);

    if (maxStock !== null && currentQty >= maxStock) {
      showValidationError(`Only ${maxStock} item(s) available in stock for "${item.name}".`);
      return;
    }

    const newQty = currentQty + 1;
    const updated = cartItems.map((it, idx) =>
      idx === index ? { ...it, quantity: newQty, subtotal: (Number(it.price) || 0) * newQty } : it
    );
    setCartItems(updated);

    // Persist to storage / backend
    if (!currentUser?.id) {
      updateGuestCartQty(item.id, newQty);
    } else {
      try {
        const cached = getUserCartCache().map((it) =>
          it.id === item.id ? { ...it, quantity: newQty } : it
        );
        saveUserCartCache(cached);
        await fetch(`${API_BASE_URL}/cart/update/${item.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: currentUser.id,
            cart_id: item.id,
            product_id: item.product_id,
            quantity: newQty,
          }),
        });
      } catch (e) {
        console.warn('Failed to update cart quantity on server:', e);
      }
    }
    window.dispatchEvent(new Event('vanya_cart_change'));
    window.dispatchEvent(new Event('storage'));
  };

  const handleDecreaseQty = async (index) => {
    const item = cartItems[index];
    if (!item) return;

    const currentQty = Number(item.quantity || 1);
    if (currentQty <= 1) {
      handleRemoveItem(index);
      return;
    }

    const newQty = currentQty - 1;
    const updated = cartItems.map((it, idx) =>
      idx === index ? { ...it, quantity: newQty, subtotal: (Number(it.price) || 0) * newQty } : it
    );
    setCartItems(updated);

    // Persist to storage / backend
    if (!currentUser?.id) {
      updateGuestCartQty(item.id, newQty);
    } else {
      try {
        const cached = getUserCartCache().map((it) =>
          it.id === item.id ? { ...it, quantity: newQty } : it
        );
        saveUserCartCache(cached);
        await fetch(`${API_BASE_URL}/cart/update/${item.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: currentUser.id,
            cart_id: item.id,
            product_id: item.product_id,
            quantity: newQty,
          }),
        });
      } catch (e) {
        console.warn('Failed to update cart quantity on server:', e);
      }
    }
    window.dispatchEvent(new Event('vanya_cart_change'));
    window.dispatchEvent(new Event('storage'));
  };

  const handleRemoveItem = async (index) => {
    const item = cartItems[index];
    if (!item) return;

    const updated = cartItems.filter((_, idx) => idx !== index);
    setCartItems(updated);

    // Remove from out of stock tracker
    setOutOfStockItemIds((prev) => {
      const next = new Set(prev);
      next.delete(Number(item.product_id || item.id));
      return next;
    });

    // Persist to storage / backend
    if (!currentUser?.id) {
      removeGuestCartItem(item.id);
    } else {
      try {
        const cached = getUserCartCache().filter((it) => it.id !== item.id);
        saveUserCartCache(cached);
        await fetch(`${API_BASE_URL}/cart/delete/${item.id}`, {
          method: 'DELETE',
        });
      } catch (e) {
        console.warn('Failed to delete cart item on server:', e);
      }
    }
    window.dispatchEvent(new Event('vanya_cart_change'));
    window.dispatchEvent(new Event('storage'));
  };

  const handleRemoveItemById = (itemId) => {
    const idx = cartItems.findIndex(
      (it) => Number(it.id) === Number(itemId) || Number(it.product_id) === Number(itemId)
    );
    if (idx !== -1) {
      handleRemoveItem(idx);
    }
  };

  const validateAddress = () => {
    const requiredFields = [
      'fullName',
      'phone',
      'email',
      'pinCode',
      'city',
      'state',
      'address',
    ];

    for (let field of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        showValidationError(
          `Please enter your ${
            field === 'fullName'
              ? 'Full Name'
              : field.charAt(0).toUpperCase() + field.slice(1)
          }`
        );
        return false;
      }
    }

    if (!/^[6-9]\d{9}$/.test(formData.phone.trim())) {
      showValidationError('Please enter a valid 10-digit Indian mobile number for delivery');
      return false;
    }

    if (formData.altPhone && formData.altPhone.trim() !== '') {
      const cleanAlt = formData.altPhone.trim().replace(/\D/g, '').slice(-10);
      if (!/^[6-9]\d{9}$/.test(cleanAlt)) {
        showValidationError('Please enter a valid 10-digit alternative mobile number, or leave it blank');
        return false;
      }
      if (cleanAlt === formData.phone.trim().replace(/\D/g, '').slice(-10)) {
        showValidationError('Alternative mobile number should be different from the main delivery mobile number');
        return false;
      }
    }

    if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      showValidationError('Please enter a valid email address');
      return false;
    }

    if (!/^\d{6}$/.test(formData.pinCode.trim())) {
      showValidationError('Please enter a valid 6 digit PIN code');
      return false;
    }

    return true;
  };

  // Helper to dynamically load Razorpay checkout script
  const loadRazorpaySDK = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const existingScript = document.getElementById('razorpay-checkout-script');
      if (existingScript) {
        existingScript.onload = () => resolve(true);
        existingScript.onerror = () => resolve(false);
        return;
      }
      const script = document.createElement('script');
      script.id = 'razorpay-checkout-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Helper to retrieve Razorpay Key ID
  const getRazorpayKeyId = async () => {
    const envKey = (process.env.REACT_APP_RAZORPAY_KEY_ID || '').trim();
    if (envKey && envKey !== 'your_razorpay_live_key_id') {
      return envKey;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/orders/razorpay/key`);
      const data = await res.json();
      if (data.success && data.key_id && data.key_id !== 'your_razorpay_live_key_id') {
        return data.key_id;
      }
    } catch (e) {
      console.warn('Could not fetch Razorpay key from backend:', e);
    }
    return envKey;
  };

  // Helper to check live stock of all cart items from database
  const checkLiveStock = async (items) => {
    try {
      const res = await fetch(`${API_BASE_URL}/orders/check-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      return data;
    } catch (e) {
      console.warn('Stock pre-check request error:', e);
      return { inStock: true, success: true };
    }
  };

  // Check stock on initial checkout page entry
  useEffect(() => {
    if (!cartItems.length) return;
    const verifyInitialStock = async () => {
      const res = await checkLiveStock(cartItems);
      if (res && !res.inStock && res.outOfStockItems?.length > 0) {
        const oosIds = new Set(res.outOfStockItems.map((it) => Number(it.product_id || it.id)));
        setOutOfStockItemIds(oosIds);
        const oos = res.outOfStockItems[0];
        showValidationError(
          `Notice: "${oos.name}" is currently out of stock (${oos.available} available). You can remove it or adjust quantity directly in your Order Summary.`,
          oos
        );
      } else {
        setOutOfStockItemIds(new Set());
      }
    };
    verifyInitialStock();
  }, [cartItems.length]);

  // Step 1 to Step 2 transition with address & live stock check
  const handleContinueToPayment = async () => {
    if (!validateAddress()) return;

    if (!cartItems.length) {
      showValidationError('Your shopping bag is empty! Please add items to proceed.');
      return;
    }

    setLoading(true);
    const stockCheck = await checkLiveStock(cartItems);
    setLoading(false);

    if (stockCheck && !stockCheck.inStock && stockCheck.outOfStockItems?.length > 0) {
      const oosIds = new Set(stockCheck.outOfStockItems.map((it) => Number(it.product_id || it.id)));
      setOutOfStockItemIds(oosIds);
      const oos = stockCheck.outOfStockItems[0];
      showValidationError(
        `Cannot proceed: "${oos.name}" is currently out of stock (${oos.available} available). Please remove it from your Order Summary or click below to remove it automatically.`,
        oos
      );
      return;
    }

    setStep('payment');
  };

  // Helper to release active payment locks
  const releasePaymentLock = useCallback((orderId = null) => {
    fetch(`${API_BASE_URL}/orders/razorpay/release-lock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: checkoutSessionId,
        razorpay_order_id: orderId,
      }),
    }).catch(() => {});
  }, [checkoutSessionId]);

  // Stop queue polling interval and timer
  const stopQueuePolling = useCallback(() => {
    if (queueIntervalRef.current) {
      clearInterval(queueIntervalRef.current);
      queueIntervalRef.current = null;
    }
    if (queueTimerRef.current) {
      clearInterval(queueTimerRef.current);
      queueTimerRef.current = null;
    }
    setGatewayQueue({ active: false, countdown: 180 });
  }, []);

  // When customer cancels or closes from the gateway queue modal
  const handleCancelQueue = () => {
    stopQueuePolling();
    releasePaymentLock();
    setLoading(false);
  };

  // Cleanup locks on unmount or tab close
  useEffect(() => {
    return () => {
      stopQueuePolling();
      releasePaymentLock();
    };
  }, [stopQueuePolling, releasePaymentLock]);

  // Gateway queue countdown timer (3 minutes)
  useEffect(() => {
    if (gatewayQueue.active) {
      queueTimerRef.current = setInterval(() => {
        setGatewayQueue((prev) => {
          if (prev.countdown <= 1) {
            stopQueuePolling();
            releasePaymentLock();
            setLoading(false);
            showValidationError('Payment gateway connection timed out. Please try proceeding again.');
            return { active: false, countdown: 180 };
          }
          return { ...prev, countdown: prev.countdown - 1 };
        });
      }, 1000);
    } else {
      if (queueTimerRef.current) {
        clearInterval(queueTimerRef.current);
        queueTimerRef.current = null;
      }
    }
    return () => {
      if (queueTimerRef.current) clearInterval(queueTimerRef.current);
    };
  }, [gatewayQueue.active, stopQueuePolling, releasePaymentLock]);

  // Launch Razorpay Payment Gateway Modal
  const launchRazorpayGateway = (orderData, keyId, richNotes, userId) => {
    const rzpOrderId = orderData.order_id || orderData.order?.id;
    const options = {
      key: keyId,
      amount: Math.round(grandTotal * 100), // in paise
      currency: 'INR',
      name: 'Vanya Collections',
      description: `Order Payment (${cartItems.length} item${cartItems.length > 1 ? 's' : ''})`,
      image: 'https://api.vanyacollectionsrajahmundry.com/uploads/logo.png',
      order_id: rzpOrderId,
      prefill: {
        name: formData.fullName.trim(),
        email: formData.email.trim(),
        contact: formData.phone.trim(),
      },
      notes: orderData.notes || richNotes,
      theme: {
        color: '#513277', // Vanya royal brand purple
      },
      modal: {
        ondismiss: function () {
          setLoading(false);
          releasePaymentLock(rzpOrderId);
        },
      },
      handler: async function (response) {
        try {
          setLoading(true);

          const verifyRes = await fetch(
            `${API_BASE_URL}/orders/razorpay/verify-and-create`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                formData,
                cartItems,
                totalAmount: grandTotal,
                shipping_fee: shippingFee,
                discount_amount: discountAmount,
                user_id: userId,
                account_phone: currentUser?.phone || null,
                account_email: currentUser?.email || null,
              }),
            }
          );

          const verifyData = await verifyRes.json();
          setLoading(false);

          if (verifyRes.ok && verifyData.success) {
            if (!userId) {
              clearGuestCart();
            }
            window.dispatchEvent(new Event('vanya_cart_change'));
            window.dispatchEvent(new Event('storage'));

            const finalOrderNumber =
              verifyData.orderNumber ||
              verifyData.order_number ||
              (verifyData.orderId ? `VAN-${String(verifyData.orderId).padStart(2, '0')}` : 'VAN-ORDER');

            setSuccessPopup({
              show: true,
              orderNumber: finalOrderNumber,
              orderId: verifyData.orderId,
              totalAmount: grandTotal,
              paidPrice: grandTotal,
              paymentId: response.razorpay_payment_id,
              customerName: formData.fullName,
              city: formData.city,
              countdown: 5,
            });
          } else {
            showValidationError(
              verifyData.message || 'Payment verification failed. Please contact our support team.'
            );
          }
        } catch (err) {
          console.error('Payment verification error:', err);
          setLoading(false);
          showValidationError(
            'Server error while verifying payment. If amount was debited, our team will confirm your order shortly.'
          );
        }
      },
    };

    const razorpayInstance = new window.Razorpay(options);

    razorpayInstance.on('payment.failed', function (failResp) {
      setLoading(false);
      releasePaymentLock(rzpOrderId);
      showValidationError(
        failResp.error?.description ||
          failResp.error?.reason ||
          'Payment failed or was cancelled. Please try again.'
      );
    });

    razorpayInstance.open();
    setLoading(false);
  };

  // Start polling every 3 seconds while in queue
  const startQueuePolling = (keyId, richNotes, userId) => {
    if (queueIntervalRef.current) clearInterval(queueIntervalRef.current);

    queueIntervalRef.current = setInterval(async () => {
      try {
        const pollRes = await fetch(`${API_BASE_URL}/orders/razorpay/check-queue-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: checkoutSessionId,
            amount: grandTotal,
            currency: 'INR',
            cartItems,
            formData,
            shipping_fee: shippingFee,
            discount_amount: discountAmount,
            user_id: userId,
            notes: richNotes,
          }),
        });

        const pollData = await pollRes.json();

        // If an item sold out, stop queue and show Out-of-Stock modal with photo & name
        if (pollData.outOfStock) {
          stopQueuePolling();
          setLoading(false);
          setOutOfStockModal({
            show: true,
            item: pollData.item,
          });
          return;
        }

        // If item became available and Razorpay order was acquired
        if (pollData.success && (pollData.order_id || pollData.order)) {
          stopQueuePolling();
          launchRazorpayGateway(pollData, keyId, richNotes, userId);
          return;
        }
      } catch (err) {
        console.warn('Queue polling check error:', err);
      }
    }, 3000);
  };

  // ==========================================================
  // PROCEED TO PAY WITH RAZORPAY
  // ==========================================================
  const handleProceedToPay = async () => {
    if (loading) return;

    if (!cartItems.length) {
      showValidationError('Your shopping bag is empty!');
      return;
    }

    if (!validateAddress()) {
      setStep('address');
      return;
    }

    setLoading(true);

    try {
      // 1. Live stock check before opening payment gateway
      const stockCheck = await checkLiveStock(cartItems);
      if (stockCheck && !stockCheck.inStock && stockCheck.outOfStockItems?.length > 0) {
        setLoading(false);
        const oosIds = new Set(stockCheck.outOfStockItems.map((it) => Number(it.product_id || it.id)));
        setOutOfStockItemIds(oosIds);
        const oos = stockCheck.outOfStockItems[0];
        setOutOfStockModal({
          show: true,
          item: {
            id: oos.product_id || oos.id,
            name: oos.name,
            image: oos.img_url || oos.image || oos.imageUrl || '',
          },
        });
        return;
      }

      // 2. Ensure Razorpay SDK is loaded
      const isSdkLoaded = await loadRazorpaySDK();
      if (!isSdkLoaded) {
        setLoading(false);
        showValidationError(
          'Unable to load Razorpay payment gateway. Please check your internet connection.'
        );
        return;
      }

      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      const userId = user ? user.id : null;

      // Helper to build rich item notes for Razorpay
      const buildCheckoutNotes = (fData, items) => {
        const notesObj = {};
        if (fData) {
          if (fData.fullName) notesObj.customer_name = String(fData.fullName).trim().slice(0, 40);
          if (fData.phone) notesObj.customer_phone = String(fData.phone).trim().slice(0, 20);
          notesObj.alternate_contact =
            fData.altPhone && String(fData.altPhone).trim()
              ? String(fData.altPhone).trim().slice(0, 20)
              : 'None';
          const addrParts = [
            fData.address,
            fData.city,
            fData.state
              ? `${fData.state}${fData.pinCode ? ` - ${fData.pinCode}` : ''}`
              : fData.pinCode,
          ].filter(Boolean);
          if (addrParts.length > 0) notesObj.shipping_address = addrParts.join(', ').slice(0, 255);
        }
        if (Array.isArray(items) && items.length > 0) {
          notesObj.items_count = String(items.length);
          const summaryParts = items.map((it) => {
            const pid = it.product_id || it.id || 'N/A';
            const q = it.quantity || it.qty || 1;
            const n = (it.name || 'Saree').slice(0, 25);
            return `[ID:${pid}] ${n} (Qty:${q})`;
          });
          notesObj.items_summary = summaryParts.join('; ').slice(0, 255);
          items.slice(0, 8).forEach((it, idx) => {
            const pid = it.product_id || it.id || 'N/A';
            const q = it.quantity || it.qty || 1;
            const p = Number(it.price || it.variant?.price || 0);
            const n = it.name || 'Saree';
            const col = it.colour || it.variant?.colour;
            const sz = it.size || it.variant?.size;
            const extra = [col ? `Col:${col}` : '', sz ? `Size:${sz}` : ''].filter(Boolean).join(', ');
            notesObj[`item_${idx + 1}`] = `[ID:${pid}] ${n}${extra ? ` (${extra})` : ''} | Qty:${q} | Rs.${p * q}`.slice(0, 255);
          });
        }
        return notesObj;
      };

      const richNotes = buildCheckoutNotes(formData, cartItems);

      // 3. Create Razorpay Order on Backend (with concurrency lock & stock check)
      const orderRes = await fetch(`${API_BASE_URL}/orders/razorpay/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: checkoutSessionId,
          amount: grandTotal,
          currency: 'INR',
          cartItems,
          formData,
          shipping_fee: shippingFee,
          discount_amount: discountAmount,
          user_id: userId,
          notes: richNotes,
        }),
      });

      const orderData = await orderRes.json();

      // Check if item is out of stock (show photo, name, out of stock)
      if (orderData.outOfStock) {
        setLoading(false);
        setOutOfStockModal({
          show: true,
          item: orderData.item,
        });
        return;
      }

      const keyId = orderData.key_id || (await getRazorpayKeyId());

      if (!keyId || keyId === 'your_razorpay_live_key_id') {
        setLoading(false);
        showValidationError(
          'Razorpay Live Key ID is not configured yet. Please configure RAZORPAY_KEY_ID in .env.'
        );
        return;
      }

      // Check if placed in Gateway Queue (Proceeding to payment gateway...)
      if (orderData.queued) {
        setGatewayQueue({
          active: true,
          countdown: orderData.waitEstimatedSeconds || 180,
        });
        startQueuePolling(keyId, richNotes, userId);
        return;
      }

      if (!orderRes.ok || !orderData.success) {
        setLoading(false);
        showValidationError(
          orderData.message || 'Failed to initialize Razorpay payment. Please try again.'
        );
        return;
      }

      // 4. Stock is available freely -> launch Razorpay gateway immediately
      launchRazorpayGateway(orderData, keyId, richNotes, userId);
    } catch (err) {
      console.error('Razorpay invocation error:', err);
      setLoading(false);
      showValidationError(
        err.message || 'Unable to open Razorpay payment gateway. Please try again.'
      );
    }
  };

  const handleCopyOrderId = (id) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(id);
      setCopiedOrderId(true);
      setTimeout(() => setCopiedOrderId(false), 2000);
    }
  };

  return (
    <div className="checkout-container">
      <style>{`
        .checkout-container {
          background: linear-gradient(135deg, #441e57 0%, #633878 50%, #351545 100%);
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 40px 5%;
          color: #fcfcfc;
          box-sizing: border-box;
        }

        .checkout-title {
          font-family: 'Playfair Display', serif;
          font-size: 36px;
          margin-bottom: 30px;
          color: #d4af37;
          text-align: center;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-shadow: 0 2px 10px rgba(0,0,0,0.3);
        }

        .checkout-layout {
          display: grid;
          grid-template-columns: 1.8fr 1fr;
          gap: 35px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .white-card {
          background: #ffffff;
          padding: 32px;
          border-radius: 24px;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.25);
          border: 1.5px solid rgba(212, 175, 55, 0.4);
          color: #333;
        }

        .section-header {
          font-family: 'Playfair Display', serif;
          font-size: 24px;
          margin-top: 0;
          margin-bottom: 22px;
          color: #513277;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 2px solid #f2eaf7;
          padding-bottom: 12px;
        }

        .step-pill {
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 700;
          background: #f4ecf8;
          color: #513277;
          padding: 4px 12px;
          border-radius: 20px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .account-prefill-badge {
          background: #fdfaf3;
          border: 1.5px solid #ebd9a2;
          border-radius: 14px;
          padding: 14px 18px;
          margin-bottom: 22px;
          display: flex;
          flex-direction: column;
          gap: 5px;
          font-size: 13.5px;
          color: #513277;
          box-shadow: 0 4px 12px rgba(212, 175, 55, 0.08);
        }

        .account-prefill-badge strong {
          color: #2b113f;
        }

        .account-prefill-hint {
          font-size: 12px;
          color: #795d38;
          line-height: 1.4;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-size: 11px;
          font-weight: 700;
          color: #513277;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        .form-group input,
        .form-group textarea {
          padding: 13px 16px;
          border: 1.5px solid #dcdcdc;
          border-radius: 12px;
          background: #ffffff;
          font-size: 14.5px;
          outline: none;
          width: 100%;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
          color: #222;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          border-color: #513277;
          box-shadow: 0 0 0 3px rgba(81, 50, 119, 0.15);
        }

        /* RAZORPAY PAYMENT CARD STYLING */
        .rzp-payment-card {
          background: linear-gradient(135deg, #fdfbfa 0%, #fff8eb 100%);
          border: 2px solid #d4af37;
          border-radius: 20px;
          padding: 24px;
          box-shadow: 0 8px 25px rgba(212, 175, 55, 0.15);
          margin-bottom: 25px;
          position: relative;
          overflow: hidden;
        }

        .rzp-payment-card::before {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 140px;
          height: 140px;
          background: radial-gradient(circle, rgba(212, 175, 55, 0.15) 0%, transparent 70%);
          pointer-events: none;
        }

        .rzp-header-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 16px;
        }

        .rzp-title-wrap {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .rzp-icon-box {
          width: 48px;
          height: 48px;
          background: #513277;
          color: #d4af37;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          box-shadow: 0 4px 12px rgba(81, 50, 119, 0.25);
          flex-shrink: 0;
        }

        .rzp-title-wrap h3 {
          margin: 0;
          font-size: 18px;
          color: #513277;
          font-weight: 700;
        }

        .rzp-title-wrap p {
          margin: 3px 0 0 0;
          font-size: 12.5px;
          color: #666;
        }

        .rzp-live-badge {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .rzp-live-dot {
          width: 7px;
          height: 7px;
          background: #10b981;
          border-radius: 50%;
          box-shadow: 0 0 6px #10b981;
          animation: pulseDot 1.5s infinite;
        }

        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.85); }
        }

        .rzp-methods-strip {
          background: #ffffff;
          border: 1px solid #eadbc8;
          border-radius: 14px;
          padding: 14px 16px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 16px;
        }

        .rzp-method-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .method-tag {
          background: #fbf8fc;
          border: 1px solid #e5d7ec;
          color: #513277;
          padding: 5px 10px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }

        .rzp-security-note {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: #555;
          line-height: 1.5;
        }

        .address-preview-box {
          background: #faf7fc;
          border: 1px solid #e6d8ed;
          border-radius: 14px;
          padding: 14px 16px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
        }

        .address-preview-text {
          font-size: 13px;
          color: #333;
          line-height: 1.4;
        }

        .address-preview-text strong {
          color: #513277;
        }

        .edit-addr-btn {
          background: none;
          border: 1px solid #513277;
          color: #513277;
          padding: 6px 14px;
          border-radius: 18px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: 0.2s;
        }

        .edit-addr-btn:hover {
          background: #513277;
          color: #fff;
        }

        .primary-btn {
          width: 100%;
          background: #513277;
          color: #d4af37;
          padding: 16px 20px;
          border: none;
          border-radius: 35px;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.25s ease;
          box-shadow: 0 6px 20px rgba(81, 50, 119, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          box-sizing: border-box;
        }

        .primary-btn.gold {
          background: linear-gradient(135deg, #d4af37 0%, #f6dd82 50%, #d4af37 100%);
          color: #3b1959;
          box-shadow: 0 8px 25px rgba(212, 175, 55, 0.4);
          font-size: 17px;
          letter-spacing: 0.3px;
        }

        .primary-btn:hover {
          opacity: 0.96;
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(81, 50, 119, 0.35);
        }

        .primary-btn.gold:hover {
          box-shadow: 0 12px 35px rgba(212, 175, 55, 0.55);
        }

        .primary-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        /* ORDER SUMMARY CARD */
        .summary-card {
          background: #ffffff;
          padding: 28px;
          border-radius: 24px;
          border: 1.5px solid rgba(212, 175, 55, 0.4);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.25);
          height: fit-content;
          color: #333;
        }

        .summary-title {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          color: #513277;
          margin-top: 0;
          margin-bottom: 20px;
          border-bottom: 2px solid #f0f0f0;
          padding-bottom: 12px;
        }

        .item-row {
          display: flex;
          gap: 14px;
          padding-bottom: 14px;
          margin-bottom: 14px;
          border-bottom: 1px solid #f2f2f2;
          align-items: center;
        }

        .item-row img {
          width: 58px;
          height: 72px;
          object-fit: cover;
          border-radius: 10px;
          flex-shrink: 0;
          border: 1px solid #eee;
        }

        .item-details h5 {
          margin: 0 0 4px 0;
          font-size: 14px;
          color: #222;
          font-weight: 600;
        }

        .item-details p {
          margin: 0;
          font-size: 12.5px;
          color: #666;
        }

        .checkout-qty-stepper {
          display: inline-flex;
          align-items: center;
          background: #fdfaf6;
          border: 1.5px solid #d4af37;
          border-radius: 20px;
          padding: 2px 4px;
          gap: 6px;
        }

        .checkout-qty-stepper button {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: none;
          background: #513277;
          color: #ffffff;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          transition: background 0.15s, transform 0.15s;
          padding: 0;
        }

        .checkout-qty-stepper button:hover:not(:disabled) {
          background: #74418c;
          transform: scale(1.08);
        }

        .checkout-qty-stepper button:disabled {
          background: #d1c7d8;
          color: #888;
          cursor: not-allowed;
        }

        .checkout-qty-stepper span {
          font-size: 13px;
          font-weight: 700;
          color: #513277;
          min-width: 16px;
          text-align: center;
        }

        .checkout-remove-btn {
          background: #fff5f5;
          border: 1px solid #fecaca;
          color: #dc2626;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          padding: 0;
        }

        .checkout-remove-btn:hover {
          background: #dc2626;
          color: #ffffff;
          border-color: #dc2626;
          transform: scale(1.08);
        }

        .checkout-oos-badge {
          background: #fee2e2;
          color: #b91c1c;
          border: 1px solid #f87171;
          border-radius: 6px;
          padding: 3px 7px;
          font-size: 10.5px;
          font-weight: 700;
          display: inline-block;
          margin-top: 5px;
        }

        .summary-calc {
          display: flex;
          flex-direction: column;
          gap: 10px;
          font-size: 14px;
          color: #555;
          margin-top: 15px;
        }

        .calc-line {
          display: flex;
          justify-content: space-between;
        }

        .total-row {
          display: flex;
          justify-content: space-between;
          margin-top: 18px;
          padding-top: 16px;
          border-top: 2px dashed #e5d7ec;
          align-items: center;
        }

        .total-row span {
          font-weight: 700;
          color: #333;
          font-size: 16px;
        }

        .total-value {
          font-size: 24px;
          font-weight: 800;
          color: #513277;
        }

        .trust-seals-grid {
          margin-top: 20px;
          padding-top: 18px;
          border-top: 1px solid #f0f0f0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          font-size: 11px;
          color: #666;
        }

        .trust-seal {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        /* GORGEOUS SUCCESS POPUP MODAL */
        .success-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(18, 6, 26, 0.75);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          padding: 20px;
          box-sizing: border-box;
          animation: fadeIn 0.3s ease;
        }

        .success-modal {
          background: #ffffff;
          width: 100%;
          max-width: 480px;
          border-radius: 28px;
          padding: 0;
          overflow: hidden;
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.45);
          border: 2px solid #d4af37;
          animation: scaleUp 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          text-align: center;
          color: #333;
        }

        .success-modal-header {
          background: linear-gradient(135deg, #513277 0%, #361754 100%);
          padding: 35px 25px 28px;
          color: #ffffff;
          position: relative;
        }

        .success-checkmark-ring {
          width: 72px;
          height: 72px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 38px;
          color: #ffffff;
          margin: 0 auto 16px;
          box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.15), 0 8px 25px rgba(16, 185, 129, 0.45);
          animation: bounceIn 0.6s ease;
        }

        .success-modal-header h2 {
          font-family: 'Playfair Display', serif;
          font-size: 26px;
          color: #d4af37;
          margin: 0 0 6px;
          letter-spacing: 0.5px;
        }

        .success-modal-header p {
          margin: 0;
          font-size: 13.5px;
          color: #e3d5ec;
        }

        .success-modal-body {
          padding: 24px 26px;
          text-align: left;
        }

        .success-detail-card {
          background: #faf6fd;
          border: 1.5px solid #e9d9f1;
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 7px 0;
          border-bottom: 1px dashed #e2cfea;
          font-size: 13.5px;
        }

        .detail-row:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .detail-row .label {
          color: #666;
          font-weight: 500;
        }

        .detail-row .value {
          font-weight: 700;
          color: #513277;
        }

        .detail-row .value.highlight {
          color: #059669;
          font-size: 16px;
        }

        .order-id-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #513277;
          color: #d4af37;
          padding: 4px 10px;
          border-radius: 8px;
          font-family: monospace;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: 0.2s;
        }

        .order-id-badge:hover {
          background: #3b1959;
        }

        .countdown-notice {
          text-align: center;
          font-size: 12.5px;
          color: #777;
          margin-bottom: 18px;
        }

        .countdown-number {
          font-weight: 700;
          color: #513277;
        }

        .countdown-bar {
          width: 100%;
          height: 4px;
          background: #f0e6f5;
          border-radius: 2px;
          overflow: hidden;
          margin-top: 6px;
        }

        .countdown-progress {
          height: 100%;
          background: linear-gradient(90deg, #513277, #d4af37);
          transition: width 1s linear;
        }

        /* SIMPLE NOTICE POPUP */
        .popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          box-sizing: border-box;
          animation: fadeIn 0.25s ease;
        }

        .validation-popup {
          width: 100%;
          max-width: 380px;
          background: white;
          border-radius: 22px;
          padding: 28px 24px;
          text-align: center;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.35);
          animation: scaleUp 0.25s ease;
          border: 1px solid rgba(212, 175, 55, 0.3);
        }

        .validation-popup h3 {
          color: #513277;
          margin: 0 0 10px;
          font-size: 20px;
        }

        .validation-popup p {
          color: #555;
          font-size: 14px;
          margin: 0 0 20px;
          line-height: 1.5;
        }

        .validation-popup button {
          width: 100%;
          padding: 12px;
          border: none;
          border-radius: 25px;
          background: #513277;
          color: #d4af37;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          transition: 0.2s;
        }

        .validation-popup button:hover {
          opacity: 0.95;
        }

        /* GATEWAY QUEUE BUFFER MODAL */
        .gateway-queue-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(18, 6, 26, 0.85);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          padding: 20px;
          box-sizing: border-box;
          animation: fadeIn 0.3s ease;
        }

        .gateway-queue-modal {
          background: #ffffff;
          width: 100%;
          max-width: 440px;
          border-radius: 26px;
          padding: 36px 28px 28px;
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.45);
          border: 2px solid #d4af37;
          text-align: center;
          animation: scaleUp 0.3s ease;
          color: #333;
        }

        .gateway-spinner-wrap {
          position: relative;
          width: 80px;
          height: 80px;
          margin: 0 auto 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .gateway-spinner-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 4px solid #f4ecf8;
          border-top-color: #513277;
          border-right-color: #d4af37;
          animation: spin 1s cubic-bezier(0.55, 0.15, 0.45, 0.85) infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .gateway-lock-icon {
          font-size: 28px;
          animation: pulseLock 2s infinite ease-in-out;
        }

        @keyframes pulseLock {
          0%, 100% { transform: scale(1); opacity: 0.9; }
          50% { transform: scale(1.1); opacity: 1; }
        }

        .gateway-queue-title {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          color: #513277;
          margin: 0 0 10px;
          font-weight: 700;
        }

        .gateway-queue-subtext {
          font-size: 13.5px;
          color: #666;
          line-height: 1.5;
          margin: 0 0 18px;
        }

        .gateway-timer-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #faf6fd;
          border: 1.5px solid #d4af37;
          padding: 7px 16px;
          border-radius: 20px;
          font-size: 12.5px;
          font-weight: 700;
          color: #513277;
          margin-bottom: 18px;
        }

        .gateway-pulse-dot {
          width: 8px;
          height: 8px;
          background: #d4af37;
          border-radius: 50%;
          animation: pulseDot 1.2s infinite;
        }

        .gateway-warning-box {
          background: #fffbeb;
          border: 1px solid #fde68a;
          color: #92400e;
          padding: 11px 14px;
          border-radius: 12px;
          font-size: 12px;
          line-height: 1.45;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 20px;
          text-align: left;
          font-weight: 600;
        }

        .gateway-cancel-btn {
          background: transparent;
          border: 1.5px solid #e5d7ec;
          color: #777;
          padding: 10px 18px;
          border-radius: 25px;
          font-size: 12.5px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
        }

        .gateway-cancel-btn:hover {
          background: #fdf2f2;
          color: #dc2626;
          border-color: #fecaca;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleUp {
          from { transform: scale(0.85); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @keyframes bounceIn {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }

        /* RESPONSIVE DESIGN FOR PHONE AND TABLET */
        @media (max-width: 992px) {
          .checkout-container {
            padding: 16px 10px;
          }

          .checkout-title {
            font-size: 25px;
            margin-bottom: 16px;
          }

          .checkout-layout {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .white-card,
          .summary-card {
            padding: 20px 16px;
            border-radius: 18px;
          }

          .form-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .section-header {
            font-size: 19px;
            margin-bottom: 16px;
          }

          .form-group label {
            font-size: 10.5px;
          }

          .form-group input,
          .form-group textarea {
            font-size: 14px;
            padding: 11px 13px;
          }

          .rzp-payment-card {
            padding: 18px 14px;
          }

          .rzp-header-row {
            flex-direction: column;
            align-items: flex-start;
          }

          .rzp-methods-strip {
            padding: 10px 12px;
          }

          .primary-btn {
            padding: 15px;
            font-size: 15.5px;
            border-radius: 28px;
          }
        }
      `}</style>

      <h1 className="checkout-title">Vanya Collections Checkout</h1>

      <div className="checkout-layout">
        {/* LEFT COLUMN: ADDRESS & PAYMENT FORM */}
        <div className="white-card">
          {step === 'address' ? (
            <div className="address-section">
              <div className="section-header">
                <span>Delivery Address</span>
                <span className="step-pill">Step 1 of 2</span>
              </div>

              {currentUser && (
                <div className="account-prefill-badge">
                  <span>
                    👑 <strong>{currentUser.fullName || currentUser.name || 'Valued Customer'}</strong> (Account Phone: +91 {currentUser.phone || 'N/A'})
                  </span>
                  <span className="account-prefill-hint">
                    Address and contact have been automatically pre-filled. You can change the delivery mobile number or add an alternative number below without changing your account login number.
                  </span>
                </div>
              )}

              <div className="form-grid">
                {[
                  {
                    field: 'fullName',
                    label: 'Full Name',
                    type: 'text',
                    mode: 'text',
                    placeholder: 'Enter recipient full name',
                  },
                  {
                    field: 'phone',
                    label: 'Main Mobile Number (10 digits)',
                    type: 'tel',
                    mode: 'numeric',
                    placeholder: '10-digit mobile number for delivery',
                  },
                  {
                    field: 'altPhone',
                    label: 'Alternative Mobile Number (Optional)',
                    type: 'tel',
                    mode: 'numeric',
                    placeholder: 'Secondary contact number (optional)',
                  },
                  {
                    field: 'email',
                    label: 'Email Address',
                    type: 'email',
                    mode: 'email',
                    placeholder: 'Order invoice & status updates email',
                  },
                  {
                    field: 'pinCode',
                    label: 'PIN Code (6 digits)',
                    type: 'tel',
                    mode: 'numeric',
                    placeholder: '6-digit pincode',
                  },
                  {
                    field: 'city',
                    label: 'City',
                    type: 'text',
                    mode: 'text',
                    placeholder: 'City / Town',
                  },
                  {
                    field: 'state',
                    label: 'State',
                    type: 'text',
                    mode: 'text',
                    placeholder: 'State',
                  },
                ].map((item) => (
                  <div key={item.field} className="form-group">
                    <label>{item.label}</label>
                    <input
                      type={item.type}
                      inputMode={item.mode}
                      placeholder={item.placeholder}
                      value={formData[item.field] || ''}
                      onChange={(e) => {
                        if (item.field === 'pinCode') {
                          handlePincodeChange(e.target.value);
                        } else {
                          setFormData({
                            ...formData,
                            [item.field]: e.target.value,
                          });
                        }
                      }}
                    />
                    {item.field === 'pinCode' && pincodeLoading && (
                      <div style={{ fontSize: '11px', color: '#c5a059', marginTop: '4px', fontWeight: 600 }}>
                        ⏳ Fetching city & state from postal database...
                      </div>
                    )}
                    {item.field === 'pinCode' && pincodeMessage && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: pincodeMessage.type === 'success' ? '#16a34a' : '#dc2626',
                          marginTop: '4px',
                          fontWeight: 600,
                        }}
                      >
                        {pincodeMessage.text}
                      </div>
                    )}
                  </div>
                ))}

                <div className="form-group full-width">
                  <label>House No., Building, Street / Area</label>
                  <textarea
                    rows="2"
                    placeholder="Enter complete street address for doorstep delivery"
                    value={formData.address || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        address: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <button
                className="primary-btn"
                style={{ marginTop: '22px' }}
                onClick={handleContinueToPayment}
                disabled={loading}
              >
                {loading ? 'Verifying inventory...' : 'Continue to Payment →'}
              </button>
            </div>
          ) : (
            <div className="payment-section">
              <div className="section-header">
                <span>Payment Method</span>
                <span className="step-pill">Step 2 of 2</span>
              </div>

              {/* Delivery Address Preview */}
              <div className="address-preview-box">
                <div className="address-preview-text">
                  Deliver to: <strong>{formData.fullName}</strong> • Main: {formData.phone}
                  {formData.altPhone ? ` • Alt: ${formData.altPhone}` : ''}
                  <br />
                  <span style={{ color: '#666', fontSize: '12px' }}>
                    {formData.address}, {formData.city}, {formData.state} - {formData.pinCode}
                  </span>
                </div>
                <button
                  className="edit-addr-btn"
                  onClick={() => setStep('address')}
                >
                  Edit Address
                </button>
              </div>

              {/* PREMIER RAZORPAY INTEGRATED CARD */}
              <div className="rzp-payment-card">
                <div className="rzp-header-row">
                  <div className="rzp-title-wrap">
                    <div className="rzp-icon-box">⚡</div>
                    <div>
                      <h3>Online Payment via Razorpay</h3>
                      <p>Instant confirmation • All payment options supported</p>
                    </div>
                  </div>

                  <div className="rzp-live-badge">
                    <span className="rzp-live-dot" />
                    <span>Razorpay Live Gateway</span>
                  </div>
                </div>

                <div className="rzp-methods-strip">
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>
                    Accepted Payment Modes:
                  </span>
                  <div className="rzp-method-tags">
                    <span className="method-tag">📱 UPI / PhonePe / GPay</span>
                    <span className="method-tag">💳 Credit & Debit Cards</span>
                    <span className="method-tag">🏛️ NetBanking</span>
                    <span className="method-tag">👛 Paytm & Wallets</span>
                  </div>
                </div>

                <div className="rzp-security-note">
                  <span>🔒</span>
                  <span>
                    Your payment is processed through Razorpay's bank-grade 256-bit encrypted checkout. No sensitive card details are stored on our servers.
                  </span>
                </div>
              </div>

              {/* PROCEED TO PAY BUTTON */}
              <button
                className="primary-btn gold"
                onClick={handleProceedToPay}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: '2px solid rgba(81, 50, 119, 0.3)',
                        borderTop: '2px solid #513277',
                        animation: 'spin 0.8s linear infinite',
                        display: 'inline-block',
                      }}
                    />
                    <span>Connecting to Razorpay...</span>
                  </>
                ) : (
                  <>
                    <span>Proceed to Pay — ₹{grandTotal.toLocaleString()}</span>
                    <span>🔒</span>
                  </>
                )}
              </button>

              <button
                onClick={() => setStep('address')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  fontSize: '13px',
                  width: '100%',
                  marginTop: '15px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                ← Back to Delivery Address
              </button>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: ORDER SUMMARY */}
        <div className="summary-card">
          <h3 className="summary-title">Order Summary</h3>

          <div
            className="summary-items-list"
            style={{ maxHeight: '340px', overflowY: 'auto' }}
          >
            {cartItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 10px', color: '#666' }}>
                <p style={{ fontSize: '15px', color: '#513277', fontWeight: 600 }}>Your bag is currently empty.</p>
                <button
                  type="button"
                  className="primary-btn"
                  style={{ marginTop: '15px', padding: '10px 20px', fontSize: '14px' }}
                  onClick={() => navigate('/shop')}
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              cartItems.map((item, index) => {
                const itemImg =
                  item.img_url ||
                  item.image ||
                  item.imageUrl ||
                  item.img ||
                  item.mainImage ||
                  (item.variant &&
                    (item.variant.mainImage ||
                      item.variant.img_url ||
                      item.variant.image)) ||
                  (Array.isArray(item.thumbnails) && item.thumbnails[0]) ||
                  '';

                const isItemOos =
                  outOfStockItemIds.has(Number(item.product_id || item.id)) ||
                  (item.stock !== undefined && item.stock !== null && Number(item.stock) <= 0) ||
                  (item.variant?.stock !== undefined && item.variant?.stock !== null && Number(item.variant.stock) <= 0);

                const itemMaxStock =
                  item.variant?.stock !== undefined && item.variant?.stock !== null
                    ? Number(item.variant.stock)
                    : (item.stock !== undefined && item.stock !== null ? Number(item.stock) : null);

                return (
                  <div
                    key={item.id || index}
                    className="item-row"
                    style={isItemOos ? { background: '#fff5f5', borderRadius: '12px', padding: '10px', border: '1px solid #fecaca' } : {}}
                  >
                    {itemImg ? (
                      <img
                        src={itemImg}
                        alt={item.name}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.nextSibling) {
                            e.currentTarget.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div
                      style={{
                        width: 50,
                        height: 60,
                        display: itemImg ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f7f2fa',
                        border: '1px solid #e2d9ec',
                        borderRadius: 8,
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#633878',
                        flexShrink: 0,
                      }}
                    >
                      VANYA
                    </div>
                    <div className="item-details" style={{ flex: 1 }}>
                      <h5>{item.name}</h5>
                      {(item.colour || item.variant?.colour) && (
                        <p style={{ fontSize: '11px', color: '#777', margin: '2px 0' }}>
                          Colour: {item.colour || item.variant?.colour}
                        </p>
                      )}
                      {item.size && (
                        <p style={{ fontSize: '11px', color: '#777', margin: '2px 0' }}>
                          Size: {item.size}
                        </p>
                      )}

                      {isItemOos && (
                        <div className="checkout-oos-badge">
                          ⚠️ Currently Out of Stock (0 available)
                        </div>
                      )}

                      {/* Stepper, Subtotal, and Remove Button */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', gap: '8px' }}>
                        <div className="checkout-qty-stepper">
                          <button
                            type="button"
                            onClick={() => handleDecreaseQty(index)}
                            title={item.quantity === 1 ? "Remove item" : "Decrease quantity"}
                          >
                            −
                          </button>
                          <span>{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => handleIncreaseQty(index)}
                            disabled={isItemOos || (itemMaxStock !== null && item.quantity >= itemMaxStock)}
                            title={itemMaxStock !== null && item.quantity >= itemMaxStock ? "Max stock reached" : "Increase quantity"}
                          >
                            +
                          </button>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ color: '#513277', fontSize: '13.5px' }}>
                            ₹{((Number(item.price) || 0) * (Number(item.quantity) || 1)).toLocaleString()}
                          </strong>

                          <button
                            type="button"
                            className="checkout-remove-btn"
                            onClick={() => handleRemoveItem(index)}
                            title="Remove item from order"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="summary-calc">
            <div className="calc-line">
              <span>Subtotal</span>
              <span>₹{subtotal.toLocaleString()}</span>
            </div>
            {discountAmount > 0 && (
              <div className="calc-line" style={{ color: '#2e7d32' }}>
                <span>Coupon {appliedCoupon ? `(${appliedCoupon.code})` : 'Discount'}</span>
                <span>-₹{discountAmount.toLocaleString()}</span>
              </div>
            )}
            <div className="calc-line">
              <div>
                <span>Shipping</span>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                  {totalPieces} {totalPieces > 1 ? 'pieces' : 'piece'}
                  {formData.state ? ` • ${formData.state}` : ''}
                  {shippingInfo.matchedRule && !isFreeShipping ? ` (${shippingInfo.matchedRule})` : ''}
                </div>
              </div>
              {isFreeShipping ? (
                <span style={{ color: '#2e7d32', fontWeight: 600 }}>FREE</span>
              ) : (
                <span style={{ fontWeight: 600 }}>₹{shippingFee.toLocaleString()}</span>
              )}
            </div>
          </div>

          <div className="total-row">
            <span>Total to Pay</span>
            <span className="total-value">₹{grandTotal.toLocaleString()}</span>
          </div>

          <div className="trust-seals-grid">
            <div className="trust-seal">
              <span>🛡️</span>
              <span>100% Authentic Sarees</span>
            </div>
            <div className="trust-seal">
              <span>⚡</span>
              <span>Instant Confirmation</span>
            </div>
            <div className="trust-seal">
              <span>🚚</span>
              <span>Fast Doorstep Delivery</span>
            </div>
            <div className="trust-seal">
              <span>🔒</span>
              <span>Razorpay Verified</span>
            </div>
          </div>
        </div>
      </div>

      {/* ==========================================================
          BEAUTIFUL THEME SUCCESS POPUP MODAL
      ========================================================== */}
      {successPopup.show && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-modal-header">
              <div className="success-checkmark-ring">✓</div>
              <h2>Payment Successful!</h2>
              <p>Thank you for shopping with Vanya Collections</p>
            </div>

            <div className="success-modal-body">
              <div className="success-detail-card">
                <div className="detail-row">
                  <span className="label">Order ID:</span>
                  <span
                    className="order-id-badge"
                    title="Click to copy"
                    onClick={() => handleCopyOrderId(successPopup.orderNumber || successPopup.orderId)}
                  >
                    {successPopup.orderNumber || successPopup.orderId}
                    <span style={{ fontSize: '10px' }}>
                      {copiedOrderId ? '✓ Copied' : '📋'}
                    </span>
                  </span>
                </div>

                <div className="detail-row">
                  <span className="label">Amount Paid:</span>
                  <span className="value highlight">
                    ₹{Number(successPopup.paidPrice || successPopup.totalAmount).toLocaleString()}
                  </span>
                </div>

                <div className="detail-row">
                  <span className="label">Payment ID:</span>
                  <span className="value" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {successPopup.paymentId || 'Verified'}
                  </span>
                </div>

                <div className="detail-row">
                  <span className="label">Payment Method:</span>
                  <span className="value" style={{ color: '#047857' }}>
                    ⚡ Razorpay Online
                  </span>
                </div>

                {successPopup.customerName && (
                  <div className="detail-row">
                    <span className="label">Deliver To:</span>
                    <span className="value" style={{ fontSize: '12.5px' }}>
                      {successPopup.customerName} {successPopup.city ? `(${successPopup.city})` : ''}
                    </span>
                  </div>
                )}
              </div>

              <div className="countdown-notice">
                Redirecting to order tracking in{' '}
                <span className="countdown-number">{successPopup.countdown}</span> seconds...
                <div className="countdown-bar">
                  <div
                    className="countdown-progress"
                    style={{ width: `${(successPopup.countdown / 5) * 100}%` }}
                  />
                </div>
              </div>

              <button
                className="primary-btn gold"
                onClick={() => redirectToTracking(successPopup.orderNumber || successPopup.orderId)}
              >
                Track My Order Now →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VALIDATION / NOTICE POPUP */}
      {validationPopup.show && (
        <div className="popup-overlay">
          <div className="validation-popup">
            <h3>{validationPopup.type === 'error' ? 'Notice' : 'Success'}</h3>
            <p>{validationPopup.message}</p>
            {validationPopup.actionItem ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  type="button"
                  style={{
                    background: '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '25px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    handleRemoveItemById(validationPopup.actionItem.product_id || validationPopup.actionItem.id);
                    setValidationPopup({ show: false, message: '', type: 'error', actionItem: null });
                  }}
                >
                  Remove "{validationPopup.actionItem.name}"
                </button>
                <button
                  type="button"
                  style={{
                    background: '#513277',
                    color: '#d4af37',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '25px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    setValidationPopup({ show: false, message: '', type: 'error', actionItem: null })
                  }
                >
                  Close
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() =>
                  setValidationPopup({ show: false, message: '', type: 'error', actionItem: null })
                }
              >
                OK
              </button>
            )}
          </div>
        </div>
      )}

      {/* GATEWAY QUEUE BUFFER MODAL */}
      {gatewayQueue.active && (
        <div className="gateway-queue-overlay">
          <div className="gateway-queue-modal">
            <div className="gateway-spinner-wrap">
              <div className="gateway-spinner-ring" />
              <div className="gateway-lock-icon">🔒</div>
            </div>

            <h3 className="gateway-queue-title">
              Proceeding to Payment Gateway...
            </h3>

            <p className="gateway-queue-subtext">
              Connecting to secure payment gateway. Please wait a moment while we prepare your checkout...
            </p>

            <div className="gateway-timer-badge">
              <span className="gateway-pulse-dot" />
              <span>Connecting... ({Math.floor(gatewayQueue.countdown / 60)}:{String(gatewayQueue.countdown % 60).padStart(2, '0')})</span>
            </div>

            <div className="gateway-warning-box">
              <span>⚠️</span>
              <span>
                Please do not click back, refresh, or close this screen.
              </span>
            </div>

            <button
              type="button"
              className="gateway-cancel-btn"
              onClick={handleCancelQueue}
            >
              Cancel & Return to Bag
            </button>
          </div>
        </div>
      )}

      {/* OUT OF STOCK ITEM MODAL */}
      {outOfStockModal.show && (
        <div className="popup-overlay">
          <div className="validation-popup" style={{ maxWidth: '420px', padding: '30px 24px' }}>
            <div style={{ marginBottom: '16px' }}>
              {outOfStockModal.item?.image ? (
                <img
                  src={outOfStockModal.item.image}
                  alt={outOfStockModal.item.name || 'Saree'}
                  style={{
                    width: '90px',
                    height: '115px',
                    objectFit: 'cover',
                    borderRadius: '12px',
                    border: '2px solid #fecaca',
                    boxShadow: '0 4px 15px rgba(220, 38, 38, 0.15)',
                    margin: '0 auto 12px',
                    display: 'block',
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '90px',
                    height: '115px',
                    borderRadius: '12px',
                    background: '#fee2e2',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '12px',
                    margin: '0 auto 12px',
                  }}
                >
                  VANYA
                </div>
              )}
              <h4 style={{ margin: '0 0 8px 0', fontSize: '17px', color: '#513277', fontFamily: "'Playfair Display', serif" }}>
                {outOfStockModal.item?.name || 'Saree'}
              </h4>
              <div
                style={{
                  background: '#fee2e2',
                  color: '#b91c1c',
                  border: '1px solid #fca5a5',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '11.5px',
                  fontWeight: 700,
                  display: 'inline-block',
                  marginBottom: '10px',
                }}
              >
                ⚠️ Currently Out of Stock
              </div>
            </div>

            <p style={{ color: '#555', fontSize: '13.5px', lineHeight: '1.5', margin: '0 0 20px 0' }}>
              This item is currently out of stock. Please remove it from your bag to continue with your checkout.
            </p>

            <button
              type="button"
              style={{
                background: '#dc2626',
                color: '#ffffff',
                border: 'none',
                padding: '13px',
                borderRadius: '25px',
                fontWeight: 700,
                cursor: 'pointer',
                width: '100%',
                fontSize: '14px',
                boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
              }}
              onClick={() => {
                if (outOfStockModal.item) {
                  handleRemoveItemById(outOfStockModal.item.id || outOfStockModal.item.product_id);
                }
                setOutOfStockModal({ show: false, item: null });
              }}
            >
              Remove Item & Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;